import { cardLabel, cardName, RANK_NAMES, RANKS, SUIT_NAMES } from '../cards.js';
import { SEQUENCE_LENGTH, SEQUENCES_TO_WIN } from './rules.js';
import { IllegalMoveError, } from './types.js';
/** Ace low, King high. */
export function rankIndex(card) {
    return RANKS.indexOf(card.rank);
}
const KING = RANKS.length - 1;
/**
 * Spider builds down by rank and ignores suit entirely: any Eight may be laid
 * on any Nine. This is the rule that makes the game generous to play and
 * miserable to untangle, because the run you have just built is only *movable*
 * if it also happens to be in suit — see `isValidRun`.
 */
export function canStack(moving, onto) {
    return rankIndex(moving) === rankIndex(onto) - 1;
}
/**
 * Is this slice liftable as a unit?
 *
 * Descending *and* all one suit. The gap between this and `canStack` is the
 * whole of Spider's difficulty, and the entire reason the suit count is the
 * difficulty setting: at one suit the two rules coincide and every sequence
 * moves, at four they come apart constantly.
 */
export function isValidRun(cards) {
    for (let i = 1; i < cards.length; i++) {
        const previous = cards[i - 1];
        const card = cards[i];
        if (card.suit !== previous.suit)
            return false;
        if (!canStack(card, previous))
            return false;
    }
    return true;
}
function requireColumn(state, index) {
    const pile = state.tableau[index];
    if (!pile)
        throw new IllegalMoveError('That column does not exist.');
    return pile;
}
/**
 * The cards a source refers to, top of the carried run first.
 *
 * Throws rather than returning null, matching the other three engines —
 * callers are already inside a try/catch that turns this into a protocol error.
 */
export function resolveSource(state, from) {
    const pile = requireColumn(state, from.index);
    if (from.depth < 1 || from.depth > pile.up.length) {
        throw new IllegalMoveError('There are not that many cards face up in that column.');
    }
    const run = pile.up.slice(pile.up.length - from.depth);
    if (!isValidRun(run))
        throw new IllegalMoveError('Those cards are not a run in one suit.');
    return run;
}
/** Can `cards` (a validated run) legally land on `to`? */
export function canPlace(state, cards, to) {
    const lead = cards[0];
    if (!lead)
        return false;
    const pile = state.tableau[to.index];
    if (!pile)
        return false;
    const onto = pile.up[pile.up.length - 1];
    // Any card, or any run, opens an empty column. Spider has no King rule —
    // which is just as well, since it also has no spare space.
    if (!onto)
        return pile.down.length === 0;
    return canStack(lead, onto);
}
/** True when a move would put a card back exactly where it came from. */
function isNoop(from, to) {
    return from.index === to.index;
}
export function validateMove(state, from, to) {
    if (state.phase !== 'playing')
        throw new IllegalMoveError('The game is over.');
    if (isNoop(from, to))
        throw new IllegalMoveError('That card is already there.');
    const cards = resolveSource(state, from);
    if (!canPlace(state, cards, to))
        throw new IllegalMoveError('That card cannot go there.');
    return cards;
}
// ---------------------------------------------------------------------------
// The stock
// ---------------------------------------------------------------------------
export function hasEmptyColumn(state) {
    return state.tableau.some((pile) => pile.down.length === 0 && pile.up.length === 0);
}
/**
 * Standard Spider refuses to deal onto an empty column, which is what stops the
 * stock being a free escape from a tangled board: you have to fill the gap you
 * were saving before you may turn more cards.
 */
export function canDeal(state) {
    if (state.phase !== 'playing')
        return false;
    if (state.stock.length === 0)
        return false;
    return state.rules.allowDealWithEmptyColumn || !hasEmptyColumn(state);
}
// ---------------------------------------------------------------------------
// Enumeration
// ---------------------------------------------------------------------------
function pushIfLegal(state, moves, from, to, cards) {
    if (isNoop(from, to))
        return;
    if (canPlace(state, cards, to))
        moves.push({ kind: 'move', from, to, cards: cards.slice() });
}
/**
 * Every move available right now.
 *
 * Deliberately exhaustive rather than clever: the board is 104 cards, and the
 * client uses this both to light up movable cards and to decide whether the
 * deal is dead.
 */
export function legalMoves(state) {
    const moves = [];
    if (state.phase !== 'playing')
        return moves;
    const sources = [];
    state.tableau.forEach((pile, index) => {
        // Every depth is a candidate until the run stops being one suit.
        for (let depth = 1; depth <= pile.up.length; depth++) {
            const run = pile.up.slice(pile.up.length - depth);
            if (!isValidRun(run))
                break;
            sources.push({ from: { zone: 'tableau', index, depth }, cards: run });
        }
    });
    for (const { from, cards } of sources) {
        for (let index = 0; index < state.tableau.length; index++) {
            pushIfLegal(state, moves, from, { zone: 'tableau', index }, cards);
        }
    }
    if (canDeal(state)) {
        moves.push({ kind: 'deal', count: Math.min(state.tableau.length, state.stock.length) });
    }
    return moves;
}
// ---------------------------------------------------------------------------
// Completed runs
// ---------------------------------------------------------------------------
/**
 * The King-to-Ace run sitting at the exposed end of a column, if there is one.
 *
 * Checked from the exposed end rather than by scanning, because that is the
 * only place a run can complete: cards only ever arrive at the bottom.
 */
export function completedRunAt(pile) {
    if (pile.up.length < SEQUENCE_LENGTH)
        return null;
    const run = pile.up.slice(pile.up.length - SEQUENCE_LENGTH);
    if (rankIndex(run[0]) !== KING)
        return null;
    // Thirteen cards, one suit, descending from the King — so the last is the Ace
    // without having to check it separately.
    return isValidRun(run) ? run : null;
}
/** All eight runs are off the table. */
export function isWon(state) {
    return state.completed.length >= SEQUENCES_TO_WIN;
}
/**
 * Is the deal genuinely dead?
 *
 * Simpler than Klondike's, which has to guess about buried cards to avoid
 * declaring a live game dead. Spider's stock is not a lottery — it deals every
 * remaining card onto the table in a known order — so "no legal move" is the
 * whole question, and dealing counts as one.
 */
export function isDeadlocked(state) {
    if (state.phase !== 'playing')
        return false;
    if (isWon(state))
        return false;
    return legalMoves(state).length === 0;
}
/** Why `cards` will not go onto `to`. */
export function describeRefusal(ctx, cards, to) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    const column = ctx.tableau[to.index];
    if (!column)
        return 'That column is not there.';
    const onto = column.up[column.up.length - 1];
    if (!onto) {
        // An empty column takes anything, so the only way to be refused by one is
        // for it to be waiting on its own face-down cards.
        return 'Turn that column over before building on it.';
    }
    const needed = RANKS[rankIndex(onto) - 1];
    if (!needed)
        return `${cardLabel(onto)} is an Ace — nothing goes under it.`;
    // Suit is never the reason here: Spider stacks by rank alone, and saying so
    // is the useful half of the sentence for anyone arriving from Klondike.
    return `${cardLabel(onto)} takes ${article(RANK_NAMES[needed])} ${RANK_NAMES[needed]}, of any suit.`;
}
/** "an Eight", "a Nine" — only Ace and Eight take "an" among the rank names. */
function article(rankName) {
    return /^[AEIOU]/.test(rankName) ? 'an' : 'a';
}
/** Why `cards` cannot be picked up at all, wherever they were headed. */
export function describeUnliftable(cards) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    if (cards.length > 1 && !isValidRun(cards)) {
        // Far and away the most common Spider confusion: the sequence looks right
        // because it *is* right, and it still will not move.
        const suits = new Set(cards.map((card) => card.suit));
        if (suits.size > 1) {
            return `Those are in sequence but not in one suit — only a run of ${SUIT_NAMES[lead.suit].toLowerCase()} moves with the ${cardName(lead)}.`;
        }
        return 'Only cards in sequence move together.';
    }
    return `Nothing will take the ${cardName(lead)} yet.`;
}
/** Why the stock will not deal. */
export function describeDealRefusal(state) {
    if (state.stock === 0)
        return 'The stock is empty — what is on the table is all there is.';
    if (state.emptyColumn)
        return 'Fill the empty column before dealing. That is the rule that gives gaps their price.';
    return 'The stock will not deal just now.';
}
//# sourceMappingURL=legal.js.map